import { Controller } from "@hotwired/stimulus"

// Polling for an import show page that advances in the background — the LV (BoQ)
// import, the Tarifvertrag PDF import and the Blockplan PDF import.
//
// The BoQ page normally leaves the "processing" view via a Turbo Stream broadcast
// from the parse/commit job, but that broadcast is lost when the job finishes
// before the client's ActionCable subscription is confirmed — common for fast CSV
// imports, which then sit on the progress bar forever until a manual refresh. The
// tariff import has no broadcast at all and relies on this alone.
//
// While the record is in a transient state this controller polls the show page
// (Turbo Stream) passing its current status. The server replaces the panel only
// when the status changed (otherwise 204), so the elapsed timer is not reset on
// every tick. The controller lives inside the replaced panel, so each swap
// re-instantiates it with the fresh status: it re-arms while transient and stays
// idle once a terminal state is reached.
const DEFAULT_TRANSIENT_STATES = ["pending", "parsing", "committing"]

export default class extends Controller {
  static values = {
    url: String,
    status: String,
    transientStates: { type: Array, default: DEFAULT_TRANSIENT_STATES },
    interval: { type: Number, default: 2500 },
  }

  connect() {
    if (!this.transientStatesValue.includes(this.statusValue)) return

    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibilityChange)
    this.scheduleTick()
  }

  disconnect() {
    this.clearTimer()
    if (this.boundVisibilityChange) {
      document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    }
  }

  scheduleTick() {
    this.clearTimer()
    if (document.hidden) return

    this.timer = setTimeout(() => this.poll(), this.intervalValue)
  }

  clearTimer() {
    if (this.timer) clearTimeout(this.timer)
    this.timer = null
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.clearTimer()
    } else {
      this.poll()
    }
  }

  async poll() {
    let body = ""
    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("known_status", this.statusValue)
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      })
      // The server can answer a poll by sending us somewhere else — the group-side
      // tariff import redirects into the prefilled definition form the moment the
      // extraction is through. fetch follows that 302 itself and hands back a full
      // HTML page, which renderStreamMessage drops without a word, leaving the page
      // on the progress bar forever. Navigate instead, and stop polling.
      if (response.redirected) {
        Turbo.visit(response.url)
        return
      }
      // 204 means unchanged — keep polling. A body means the panel (and this
      // controller) is about to be replaced, so we don't schedule another tick.
      if (response.ok && response.status !== 204) {
        body = await response.text()
      }
    } catch {
      // Network hiccups are fine — re-arm and the next tick retries.
    }

    if (body) {
      Turbo.renderStreamMessage(body)
    } else {
      this.scheduleTick()
    }
  }
}
